// @ts-nocheck
/**
 * Keeps credentials out of error reports (Sentry events, attachments,
 * breadcrumbs). Used by `sendReport` and by `Sentry.init` (`beforeSend`).
 */
export const MASK = "[Filtered]";

// body / header / query keys that carry a secret
const SECRET_KEY =
	/pass(word|wd)?|^token$|access_?token|refresh_?token|authorization|secret|cookie|^pwd$/i;

// query-string parameters to strip from URLs (reset link, tokens)
const SECRET_PARAM =
	/([?&#](?:token|access_token|refresh_token|code|password|newPassword)=)[^&#\s]*/gi;

export function isSecretKey(key: string) {
	return SECRET_KEY.test(key);
}

/** Deep copy of `value` with every secret key masked (cycle-safe) */
export function redact(value: any, seen = new WeakSet()): any {
	if (value === null || typeof value !== "object") return value;
	if (seen.has(value)) return "[Circular]";
	seen.add(value);
	// an axios error carries its request config (headers, JSON body): keep the
	// description only
	if (value instanceof Error)
		return { name: value.name, message: value.message, status: value.response?.status };
	if (Array.isArray(value)) return value.map((v) => redact(v, seen));
	if (typeof FormData !== "undefined" && value instanceof FormData)
		return "[FormData]";
	const out: any = {};
	for (const key of Object.keys(value))
		out[key] = isSecretKey(key) ? MASK : redact(value[key], seen);
	return out;
}

export function scrubUrl(url: any) {
	if (typeof url !== "string") return url;
	return url.replace(SECRET_PARAM, "$1" + MASK);
}

/** Sentry `beforeSend`: no secret in the event URL, request or breadcrumbs */
export function scrubEvent(event: any) {
	if (!event) return event;
	if (event.request) {
		event.request.url = scrubUrl(event.request.url);
		if (event.request.query_string)
			event.request.query_string =
				typeof event.request.query_string === "string"
					? scrubUrl("?" + event.request.query_string).slice(1)
					: redact(event.request.query_string);
		if (event.request.headers)
			event.request.headers = redact(event.request.headers);
		if (event.request.cookies) event.request.cookies = MASK;
		if (event.request.data) event.request.data = redact(event.request.data);
	}
	if (event.transaction) event.transaction = scrubUrl(event.transaction);
	if (Array.isArray(event.breadcrumbs))
		event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
	if (event.extra) event.extra = redact(event.extra);
	return event;
}

export function scrubBreadcrumb(crumb: any) {
	if (!crumb) return crumb;
	if (crumb.message) crumb.message = scrubUrl(crumb.message);
	if (crumb.data) {
		crumb.data = redact(crumb.data);
		for (const k of ["url", "to", "from"])
			if (crumb.data[k]) crumb.data[k] = scrubUrl(crumb.data[k]);
	}
	return crumb;
}
